import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const navigate = useNavigate();

  const token = sessionStorage.getItem('sessionToken');
  const userEmail = sessionStorage.getItem('userEmail');

  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setError('');
    setResults(null);
    setEmailSuccess('');
    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/upload/process', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.status === 401) {
        sessionStorage.clear();
        navigate('/');
        return;
      }

      const data = await res.json();
      if (!res.ok) {
        setError(data.error + (data.detail ? `\n${data.detail}` : '') + (data.expected ? `\nExpected: ${data.expected}` : ''));
        setLoading(false);
        return;
      }

      setResults(data);
    } catch (err) {
      setError('Upload failed. Try again.');
    }
    setLoading(false);
  };

  const handleDownload = async () => {
    try {
      const res = await fetch('/api/upload/download', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ columns: results.columns, data: results.data, originalFilename: results.originalFilename })
      });

      if (!res.ok) { setError('Download failed'); return; }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'enhanced_uio_results.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed');
    }
  };

  const handleEmail = async () => {
    setEmailSending(true);
    setEmailSuccess('');
    setError('');

    try {
      const res = await fetch('/api/upload/email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ columns: results.columns, data: results.data, originalFilename: results.originalFilename })
      });

      const data = await res.json();
      if (!res.ok) { setError(data.error); setEmailSending(false); return; }
      setEmailSuccess(data.message);
    } catch (err) {
      setError('Email send failed');
    }
    setEmailSending(false);
  };

  const previewRows = results ? results.data.slice(0, 25) : [];

  return (
    <div className="container">
      <h1>Enhanced UIO</h1>

      {userEmail && (
        <p className="info" style={{ marginBottom: 16 }}>
          Logged in as <strong>{userEmail}</strong>
        </p>
      )}

      <form onSubmit={handleUpload}>
        <label htmlFor="csvfile">Upload CSV File</label>
        <input
          id="csvfile"
          type="file"
          accept=".csv"
          onChange={(e) => setFile(e.target.files[0])}
        />
        <div>
          <button type="submit" disabled={!file || loading}>
            {loading ? 'Processing' : 'Upload & Match'}
          </button>
        </div>
      </form>

      {(loading || emailSending) && (
        <div className="overlay">
          <div className="overlay-content">
            <div className="spinner"></div>
            <h2>{emailSending ? 'Sending' : 'Processing'}</h2>
            <div className="overlay-sub">
              {emailSending ? 'Emailing your results' : 'Matching records against database'}
            </div>
          </div>
        </div>
      )}

      {error && <div className="error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}

      {results && (
        <>
          <div className="stats">
            <span><strong>Total:</strong> {results.stats.total}</span>
            <span><strong>Matched:</strong> {results.stats.matched}</span>
            <span><strong>Unmatched:</strong> {results.stats.unmatched}</span>
          </div>

          <div className="actions">
            <button onClick={handleDownload}>Download CSV</button>
            <button className="secondary" onClick={handleEmail} disabled={emailSending}>
              {emailSending ? 'Sending' : 'Email to Me'}
            </button>
          </div>

          {emailSuccess && <div className="success">{emailSuccess}</div>}

          <h2 style={{ marginTop: 24 }}>Preview (first 25 rows)</h2>
          <table className="results-table">
            <thead>
              <tr>
                {results.columns.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, i) => (
                <tr key={i}>
                  {results.columns.map(col => <td key={col}>{row[col] ?? ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {results.data.length > 25 && (
            <p className="info">Showing 25 of {results.data.length} rows</p>
          )}
        </>
      )}

      <div className="footer">Internal Tool</div>
    </div>
  );
}
